<template>
  <div class="br-modal">
    <div class="br-modal__dialog">
      <div class="br-modal__head">
        <span class="br-modal__title">Your {{ planType }} Estimate</span>
        <button
          v-modal
          class="button button--icon js-close-modal">
          <i class="icon icon-e-remove"/>
        </button>
      </div>
      <div class="br-modal__body">
        <div class="notice">
          <div class="notice__icon">
            <i class="icon icon-question"/>
          </div>
          <div class="notice__content">
            <p><strong>This Estimate</strong>: We will estimate your setup and monthly cost based on the options you choose below. Any applicable taxes are not included, and your fees are subject to increase with additional purchases. </p>
          </div>
        </div>
        <div class="br-pricing-estimate">
          <div
            v-if="model.plan_type !== 3"
            class="br-pricing-estimate__panel">
            <div class="br-pricing-estimate__panel__head">
              <h2>Organization Type</h2>
            </div>
            <div class="br-pricing-estimate__panel__body">
              <div class="form-group">
                <label
                  for="organization_type"
                  class="label">Which option best encompasses your organization?</label>
                <div class="select u-border-radius">
                  <select
                    id="organization_type"
                    v-model.number="model.organization_type"
                    name="organization_type">
                    <option value="1">
                      Non-Profit
                    </option>
                    <option value="2">
                      For-Profit
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div class="br-pricing-estimate__panel">
            <div class="br-pricing-estimate__panel__head">
              <h2>Community Size</h2>
            </div>
            <div class="br-pricing-estimate__panel__body">
              <p>It looks like you are looking to invite about <strong class="u-color-a">{{ model.size | numeral }}</strong> people to your networking community?</p>
              <slider
                v-model="model.size"
                type="size"
                :min="getMinimumCommunitySize"
                :max="500000"/>
            </div>
          </div>
          <div
            v-if="allowAddOns"
            class="br-pricing-estimate__panel">
            <div class="br-pricing-estimate__panel__head">
              <h2>{{ planType }} Community Options</h2>
              <button
                class="button button--circle"
                :class="{ 'button--active': estimateAddonsActive }"
                @click.prevent="toggleEstimateAddons">
                <i class="icon icon-settings-gear-2"/>
              </button>
            </div>
            <div class="br-pricing-estimate__panel__body">
              <p>Take a moment to select the items your organization might require. These selections will help us generate a more accurate price estimate.</p>
              <div
                class="br-pricing-estimate__addons"
                :class="{ 'br-pricing-estimate__addons--active': estimateAddonsActive }">
                <div class="checkbox-list checkbox-list--full">
                  <div
                    v-for="item in extras"
                    :key="item.id"
                    class="checkbox-list__item">
                    <div class="checkbox u-border-radius">
                      <input
                        :id="'item-addon-' + item.id"
                        v-model="model.add_ons"
                        :value="item"
                        type="checkbox"
                        class="checkbox__input">
                      <label
                        :for="'item-addon-' + item.id"
                        class="checkbox__label">
                        <div class="checkbox__details">
                          <div class="checkbox__details__main">{{ item.label }}</div>
                          <div class="checkbox__details__sub">{{ item.description }}</div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="br-pricing-estimate__panel">
            <div class="br-pricing-estimate__panel__head br-pricing-estimate__panel__head--break">
              <h2>Cost Breakdown</h2>
              <currency-selector v-model="model.currency"/>
            </div>
            <div class="br-pricing-estimate__panel__body">
              <div class="br-pricing-estimate__summary">
                <div
                  v-if="!isEnterprise"
                  class="br-pricing-estimate__summary__item">
                  <span class="text u-color-green"><i class="icon icon-c-check"/></span>
                  <span class="text">Non-Profit Organization</span>
                  <div class="text-group-vertical">
                    <span class="text text--highlight">Discount Applied </span>
                    <span class="text text--muted">(monthly savings)</span>
                  </div>
                </div>
                <div class="br-pricing-estimate__summary__item">
                  <span class="text u-color-green"><i class="icon icon-c-check"/></span>
                  <span class="text">{{ planType }} Networking Community ({{ model.size | numeral }} invitees)</span>
                  <div class="text-group-vertical">
                    <span class="text text--highlight">{{ formatNumber(getPerMemberCost, '0,0.00') }} / member</span>
                    <span class="text text--muted">(billed monthly)</span>
                  </div>
                </div>
                <div class="br-pricing-estimate__summary__item br-pricing-estimate__summary__item--alt">
                  <span class="text">Cost to Get Started:</span>
                  <div class="text-group-vertical">
                    <span class="text text--highlight">{{ formatNumber(getSetupFee) }}</span>
                    <span class="text text--muted">(one-time cost)</span>
                  </div>
                </div>
                <div class="br-pricing-estimate__summary__item br-pricing-estimate__summary__item--alt">
                  <span class="text">Monthly Cost:</span>
                  <div class="text-group-vertical">
                    <span class="text text--highlight">{{ formatNumber(getMonthlyCostWithAddOns) }}</span>
                    <span class="text text--muted">(billed monthly)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <button
            class="button button--block u-border-radius"
            @click.prevent.stop="redirectPage">
            Talk to Sales
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import clone from 'lodash/clone'
import Pricing from '@mixins/Pricing'
import Slider from '@components/Slider'
import ModalMixin from '@mixins/ModalMixin'
import CurrencySelector from '@components/CurrencySelector'

export default {
  components: {
    Slider,
    CurrencySelector
  },
  mixins: [
    Pricing,
    ModalMixin
  ],
  data: () => ({
    estimateAddonsActive: false
  }),
  computed: {
    allowAddOns () {
      return this.model.plan_type > 1
    },
    extras () {
      return this.addOns.filter(item => {
        if (this.model.plan_type === 2) {
          return !item.enterprise
        }

        return true
      })
    },
    getMinimumCommunitySize () {
      if (this.model.plan_type === 1) {
        return 1000
      }

      if (this.model.plan_type === 2) {
        return 4000
      }

      return 10000
    }
  },
  created () {
    this.initExchangeRate()

    if (this.modalParams && this.model.add_ons.length) {
      this.toggleEstimateAddons()
    }

    if (this.modalParams && !this.modalParams.name) {
      this.model = clone(this.modalParams)
    }

    if (this.modalParams && this.modalParams.name) {
      this.model.plan_type = this.modalParams.plan_type
      this.model.size = this.modalParams.size
    }

    if (this.isEnterprise) {
      this.model.organization_type = 2
    }
  },
  methods: {
    async redirectPage () {
      this.visit('/schedule-demo')

      this.closeModal()
    },
    toggleEstimateAddons () {
      this.estimateAddonsActive = !this.estimateAddonsActive
    }
  }
}
</script>
